import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner-native";
import { useAppBuyer } from "@components/Checkout/appBuyer";
import { useVendorSheet } from "@components/Checkout/vendor/useVendorSheet";
import {
  useVendorEventEnd,
  type VendorEventTimes,
} from "@components/Checkout/vendor/vendorEventEnd";
import LText from "@components/LText";
import LIcon from "@components/LIcon";
import { captureException } from "@utils/captureException";
import { trpc } from "@utils/trpc";
import { useData } from "../../../../../runtime/provider";
import Button from "../common/Button";
import { FOUR_BUTTON_LAYOUT_BUTTON_WIDTH } from "../../config";
import useValidatedExternalUrl from "../../utils/useValidatedExternalUrl";

/**
 * An external event's button. A 1-tap event (its access read carries a
 * purchase) gets its tickets in the app's tickets sheet, as an engine event
 * does; once nothing can be bought (it has passed, or no ticket is left on
 * sale) it is "Stay updated", as on Link in Bio, and never its external
 * link. An event that has passed is "Stay updated" by its own date: its
 * access read offers no purchase for it. Any other event (its access
 * offers no purchase) keeps its validated external link.
 *
 * While the access is read the button waits (a failed read offers to try
 * again): it never opens the external link without the server's "none".
 */
const RegisterExternal = ({
  accessId,
  extLinks,
  event,
}: {
  accessId: string;
  extLinks: string[];
  /** The event's own times and artist: whether it has passed, and whose
   *  event "Stay updated" is pressed on. */
  event: VendorEventTimes & { artistId: string };
}) => {
  const openExternal = useValidatedExternalUrl(extLinks, { accessId });
  // The purchase sheet, as a merch product's Buy opens it; with no
  // purchase, the event's link.
  const { purchase, failed, open, retry } = useVendorSheet(accessId);
  const ended = useVendorEventEnd(purchase, event);
  const onPress = () => (failed ? retry() : open(openExternal));

  if (ended) return <StayUpdated accessId={accessId} artistId={event.artistId} />;
  if (purchase === undefined && !failed)
    return <Button.Loading width={FOUR_BUTTON_LAYOUT_BUTTON_WIDTH} />;
  return (
    <Button.Button
      onPress={() => void onPress()}
      sentryEventName={
        failed ? "AccessDetail.RegisterExternalRetry" : "AccessDetail.RegisterExternal"
      }
    >
      <LIcon
        colors={{ color: "black" }}
        name={failed ? "loop" : purchase ? "ticket" : "extlink"}
        size={16}
      />
      <LText variant="Caption/C1/Medium" color="black">
        {failed ? "Try again" : purchase ? "Get tickets" : "Register"}
      </LText>
    </Button.Button>
  );
};

/** A listed event that sells nothing now: the press puts the fan on this
 *  event's list (Link in Bio's Stay updated, through the same
 *  implementation); the artist's mailing list is not touched. Pressed
 *  signed out (app-web), it asks the fan to sign in first and joins once
 *  they are, as Link in Bio's does. Pressed again, "You're on the list"
 *  takes the fan off the event's list. */
function StayUpdated({
  accessId,
  artistId,
}: {
  accessId: string;
  artistId: string;
}) {
  const buyer = useAppBuyer();
  const data = useData();
  // Pressed on this event before, here or on Link in Bio.
  const { data: earlier } =
    trpc.user.artistSubscriptions.updatesRequested.useQuery(
      { accessId },
      { enabled: buyer.accountLoaded },
    );
  const joined = earlier?.requested === true;
  // Pressed before sign-in: joined once the fan's account is in.
  const afterSignIn = useRef(false);
  const saving = useRef(false);
  const { mutateAsync: joinRequest, isPending: isJoining } =
    trpc.user.artistSubscriptions.requestEventUpdates.useMutation({
      onSuccess: async (_result, { accessId }) => {
        // A read started before the press must not replace the new answer.
        await data.utils.user.artistSubscriptions.updatesRequested.cancel({
          accessId,
        });
        data.utils.user.artistSubscriptions.updatesRequested.setData(
          { accessId },
          { requested: true },
        );
      },
    });
  const { mutateAsync: leaveRequest, isPending: isLeaving } =
    trpc.user.artistSubscriptions.leaveEventUpdates.useMutation({
      onSuccess: async (_result, { accessId }) => {
        await data.utils.user.artistSubscriptions.updatesRequested.cancel({
          accessId,
        });
        data.utils.user.artistSubscriptions.updatesRequested.setData(
          { accessId },
          { requested: false },
        );
      },
    });
  const changeRequest = useCallback(
    (requested: boolean) => {
      // Guard synchronously too: two taps can arrive before a new render.
      if (saving.current) return;
      saving.current = true;
      const request = requested
        ? joinRequest({ artistId, accessId })
        : leaveRequest({ accessId });
      request
        .finally(() => {
          saving.current = false;
        })
        .catch((error: unknown) => {
          captureException(error, {
            channel: "error",
            area: "checkout",
            operation: requested ? "stay-updated" : "stay-updated-leave",
            extra: { accessId, artistId },
          });
          toast.error("We couldn't do that. Please try again.");
        });
    },
    [joinRequest, leaveRequest, accessId, artistId],
  );
  useEffect(() => {
    if (!afterSignIn.current || !buyer.accountLoaded) return;
    afterSignIn.current = false;
    changeRequest(true);
  }, [buyer.accountLoaded, changeRequest]);
  const onPress = () => {
    if (saving.current) return;
    if (!buyer.accountLoaded) {
      afterSignIn.current = true;
      buyer.signIn();
      return;
    }
    return changeRequest(!joined);
  };
  return (
    <Button.Button
      disabled={isJoining || isLeaving}
      onPress={onPress}
      sentryEventName="AccessDetail.StayUpdated"
    >
      <LIcon
        colors={{ color: "black" }}
        name={joined ? "check" : "like_dormant"}
        size={16}
      />
      <LText variant="Caption/C1/Medium" color="black">
        {joined ? "You're on the list" : "Stay updated"}
      </LText>
    </Button.Button>
  );
}

export default RegisterExternal;
